import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { Logomark } from '@/components/layout/public-nav';
import { Turnstile, turnstileEnabled } from '@/components/turnstile';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/primitives';
import { supabase } from '@/lib/supabase';

const schema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'At least 6 characters'),
});
type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard';

  const form = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormData) => {
    if (turnstileEnabled && !captchaToken) {
      toast.error('Please complete the quick security check.');
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({
      ...values,
      options: { captchaToken: captchaToken ?? undefined },
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      return;
    }
    toast.success('Welcome back.');
    navigate(from, { replace: true });
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Step back into the sanctuary."
      side={
        <blockquote className="max-w-md font-display text-3xl italic leading-tight text-cream-50 md:text-4xl">
          "He restores my soul."
          <span className="mt-4 block text-sm not-italic uppercase tracking-[0.3em] text-cream-200/70">
            Psalm 23:3
          </span>
        </blockquote>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
          {form.formState.errors.email && (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline">
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            {...form.register('password')}
          />
          {form.formState.errors.password && (
            <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
          )}
        </div>
        <Turnstile key={captchaKey} onToken={setCaptchaToken} theme="light" />
        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      <div className="mt-8 text-center text-sm text-muted-foreground">
        New here?{' '}
        <Link to="/join" className="font-medium text-primary hover:underline">
          Join the sisterhood
        </Link>
      </div>
    </AuthShell>
  );
}

/* ---------------- Shared auth shell ---------------- */
export function AuthShell({
  children,
  eyebrow,
  title,
  side,
}: {
  children: React.ReactNode;
  eyebrow: string;
  title: string;
  side: React.ReactNode;
}) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left — form */}
      <div className="flex flex-col px-6 py-10 md:px-16 lg:px-20">
        <Link to="/" className="inline-flex items-center gap-3">
          <Logomark className="h-8 w-8 text-primary" />
          <span className="font-display text-lg">Gracefully Rebuilt</span>
        </Link>

        <div className="mx-auto my-auto w-full max-w-md py-16">
          <div className="eyebrow">{eyebrow}</div>
          <h1 className="mt-4 font-display text-4xl leading-tight md:text-5xl">{title}</h1>
          <div className="mt-10">{children}</div>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          By continuing you agree to our{' '}
          <Link to="/terms" className="underline">
            Terms
          </Link>{' '}
          &{' '}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
        </p>
      </div>

      {/* Right — scripture panel */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-terracotta-500 via-terracotta-600 to-terracotta-800 lg:block">
        <div className="absolute inset-0 bg-grain opacity-10 mix-blend-overlay" />
        <div aria-hidden className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-cream-50/10 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-sage-300/20 blur-3xl" />
        <div className="relative flex h-full items-end p-16">
          {side}
        </div>
      </div>
    </div>
  );
}
